
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Category } from "@/lib/types";
import { useCategories } from "@/hooks/useCategories";

interface CategoryFormProps {
  category?: Category;
  onSuccess: (category: Category) => void;
  onCancel: () => void;
  isModal?: boolean;
  mode: "create" | "edit";
  setLoading?: (loading: boolean) => void;
}

export function CategoryForm({ 
  category, 
  onSuccess, 
  onCancel, 
  isModal = false,
  mode,
  setLoading 
}: CategoryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { useCreateCategoryMutation, useUpdateCategoryMutation } = useCategories();
  const createCategory = useCreateCategoryMutation();
  const updateCategory = useUpdateCategoryMutation();

  const formSchema = z.object({
    name: z.string().min(1, "Name is required"),
    description: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: category?.name || "",
      description: category?.description || "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (setLoading) setLoading(true);
    setIsSubmitting(true);
    try {
      if (mode === "edit" && category) {
        // Update existing category
        const updated = await updateCategory.mutateAsync({
          id: category.id,
          name: values.name,
          description: values.description || "",
          created_at: category.created_at,
          updated_at: new Date().toISOString()
        });
        onSuccess(updated);
      } else {
        // Create new category
        const newCategory = await createCategory.mutateAsync({
          name: values.name,
          description: values.description || "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        onSuccess(newCategory);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to save category");
    } finally {
      if (setLoading) setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter category name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter a description (optional)"
                  {...field}
                  className="resize-none"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? "Saving..."
              : mode === "edit"
              ? "Update Category"
              : "Create Category"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
